<template>
  <teleport to="body">
    <transition name="dialog-fade">
      <div v-if="show" class="dialog-overlay" @click.self="handleBackdropClick">
        <transition name="dialog-scale">
          <div v-if="show" class="dialog-container" :class="[`dialog-${type}`, { 'dialog-large': large }]">
            <!-- Header -->
            <div class="dialog-header" :class="`header-${type}`">
              <div class="header-content">
                <div class="icon-wrapper" v-if="showIcon">
                  <svg v-if="type === 'error'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                  <svg v-else-if="type === 'warning'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/>
                  </svg>
                  <svg v-else-if="type === 'success'" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/>
                  </svg>
                </div>
                <slot name="header">
                  <transition name="title-fade" mode="out-in">
                    <h2 :key="title" class="dialog-title">{{ title }}</h2>
                  </transition>
                </slot>
              </div>
              <button 
                v-if="!fixed" 
                class="close-button" 
                @click="handleClose"
                aria-label="Close dialog"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                </svg>
              </button>
            </div>

            <!-- Body -->
            <div class="dialog-body-wrapper" ref="bodyWrapper" :style="bodyHeight ? { height: bodyHeight + 'px' } : {}">
              <div class="dialog-body" ref="body">
                <transition name="content-fade" mode="out-in" @enter="onContentEnter" @leave="onContentLeave">
                  <div :key="`content-${title}`" class="dialog-content-wrapper" ref="content">
                    <slot></slot>
                  </div>
                </transition>
              </div>
            </div>

            <!-- Footer/Actions -->
            <transition name="footer-fade">
              <div v-if="!fixed && showActions" class="dialog-footer">
                <slot name="actions">
                  <button class="dialog-button dialog-button-primary" @click="handleClose">
                    {{ confirmText }}
                  </button>
                </slot>
              </div>
            </transition>
          </div>
        </transition>
      </div>
    </transition>
  </teleport>
</template>

<script lang="ts">
import { defineComponent, ref, watch, nextTick, onMounted, onUpdated, onBeforeUnmount, PropType } from 'vue';

type DialogType = 'error' | 'warning' | 'info' | 'success';

/**
 * ModernDialog - Modern modal dialog with animations
 * 
 * A feature-rich dialog component with:
 * - Smooth height transitions
 * - Type-based styling (error, warning, info, success)
 * - ResizeObserver for dynamic content
 * - Backdrop click handling
 * - Fixed mode (non-dismissible)
 * - Teleported to body
 * 
 * @example
 * <modern-dialog
 *   :show="isOpen"
 *   title="Confirm Action"
 *   type="warning"
 *   :large="true"
 *   @close="isOpen = false"
 * >
 *   <p>Are you sure you want to continue?</p>
 *   <template #actions>
 *     <button class="dialog-button dialog-button-primary" @click="confirm">Confirm</button>
 *     <button class="dialog-button dialog-button-secondary" @click="isOpen = false">Cancel</button>
 *   </template>
 * </modern-dialog>
 */
export default defineComponent({
  name: 'ModernDialog',
  props: {
    /**
     * Controls dialog visibility
     */
    show: {
      type: Boolean,
      required: true,
    },
    /**
     * Dialog title
     */
    title: {
      type: String,
      default: '',
    },
    /**
     * Dialog type (affects styling and icon)
     */
    type: {
      type: String as PropType<DialogType>,
      default: 'info',
      validator: (val: string) => ['error', 'warning', 'info', 'success'].includes(val),
    },
    /**
     * If true, prevents closing
     */
    fixed: {
      type: Boolean,
      default: false,
    },
    /**
     * If true, clicking backdrop closes the dialog
     */
    closeOnClickOutside: {
      type: Boolean,
      default: true,
    },
    /**
     * If true, dialog is wider
     */
    large: {
      type: Boolean,
      default: false,
    },
    /**
     * Show icon in header
     */
    showIcon: {
      type: Boolean,
      default: true,
    },
    /**
     * Show actions footer
     */
    showActions: {
      type: Boolean,
      default: true,
    },
    /**
     * Text for default confirm button
     */
    confirmText: {
      type: String,
      default: 'OK',
    },
  },
  emits: ['close', 'update:show'],
  setup(props, { emit }) {
    // Refs
    const bodyHeight = ref<number | null>(null);
    const body = ref<HTMLElement | null>(null);
    const bodyWrapper = ref<HTMLElement | null>(null);
    const content = ref<HTMLElement | null>(null);
    
    let resizeObserver: ResizeObserver | null = null;

    // Setup ResizeObserver
    const setupResizeObserver = () => {
      if (!body.value || resizeObserver) return;
      
      resizeObserver = new ResizeObserver(() => {
        updateBodyHeight();
      });
      resizeObserver.observe(body.value);
    };

    // Update body height for smooth transitions
    const updateBodyHeight = () => {
      if (!body.value || !bodyWrapper.value) return;
      
      // Temporarily remove height constraint to measure natural height
      const wrapper = bodyWrapper.value;
      const currentHeight = wrapper.style.height;
      wrapper.style.height = 'auto';
      
      // Measure the actual content height
      const naturalHeight = body.value.scrollHeight;
      
      // Set the measured height with transition
      bodyHeight.value = naturalHeight;
      
      // Reset wrapper style
      wrapper.style.height = currentHeight;
    };

    // Content transition handlers
    const onContentEnter = (el: Element, done: () => void) => {
      nextTick(() => {
        updateBodyHeight();
        done();
      });
    };

    const onContentLeave = (el: Element, done: () => void) => {
      updateBodyHeight();
      setTimeout(done, 250);
    };

    // Close handlers
    const handleClose = () => {
      if (!props.fixed) {
        emit('close');
        emit('update:show', false);
      }
    };

    const handleBackdropClick = () => {
      if (props.closeOnClickOutside && !props.fixed) {
        handleClose();
      }
    };

    // Watch show prop
    watch(() => props.show, (newVal) => {
      if (newVal) {
        // Prevent body scroll when dialog is open
        document.body.style.overflow = 'hidden';
        // Measure and set height after dialog opens
        nextTick(() => {
          updateBodyHeight();
        });
      } else {
        document.body.style.overflow = '';
        bodyHeight.value = null;
      }
    });

    // Watch title changes
    watch(() => props.title, () => {
      nextTick(() => {
        updateBodyHeight();
      });
    });

    // Lifecycle hooks
    onMounted(() => {
      if (props.show) {
        nextTick(() => {
          updateBodyHeight();
          setupResizeObserver();
        });
      }
    });

    onUpdated(() => {
      // Setup resize observer when component updates
      if (props.show && !resizeObserver) {
        nextTick(() => {
          setupResizeObserver();
        });
      }
    });

    onBeforeUnmount(() => {
      // Ensure body scroll is restored
      document.body.style.overflow = '';
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    });

    return {
      bodyHeight,
      body,
      bodyWrapper,
      content,
      handleClose,
      handleBackdropClick,
      onContentEnter,
      onContentLeave
    };
  }
});
</script>

<style scoped>
/* ============================================
   Overlay
   ============================================ */
.dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--color-overlay);
  backdrop-filter: var(--backdrop-blur-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: var(--z-index-modal);
  padding: var(--spacing-4);
}

/* ============================================
   Dialog Container
   ============================================ */
.dialog-container {
  background: var(--color-bg-primary);
  border-radius: var(--radius-xl);
  box-shadow: var(--shadow-2xl);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  display: grid;
  grid-template-rows: auto 1fr auto;
  grid-template-columns: 1fr;
  overflow: hidden;
  position: relative;
  /* Smooth size transitions for width and height */
  transition: max-width var(--duration-base) var(--ease-smooth),
              width var(--duration-base) var(--ease-smooth);
}

.dialog-container.dialog-large {
  max-width: 700px;
}

/* ============================================
   Header
   ============================================ */
.dialog-header {
  padding: var(--spacing-6);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-4);
  border-bottom: var(--border-light);
}

.header-content {
  display: flex;
  align-items: center;
  gap: var(--spacing-3);
  flex: 1;
}

.icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex-shrink: 0;
}

.icon-wrapper svg {
  width: 24px;
  height: 24px;
}

.dialog-title {
  margin: 0;
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-text-primary);
  line-height: var(--line-height-tight);
}

/* Header Type Colors - All use unified green theme */
.header-error,
.header-warning,
.header-success,
.header-info {
  background: var(--gradient-primary);
  color: var(--color-text-inverse);
  border-bottom: none;
}

.header-error .dialog-title,
.header-error .icon-wrapper svg,
.header-warning .dialog-title,
.header-warning .icon-wrapper svg,
.header-success .dialog-title,
.header-success .icon-wrapper svg,
.header-info .dialog-title,
.header-info .icon-wrapper svg {
  color: var(--color-text-inverse);
}

.close-button {
  background: transparent;
  border: none;
  cursor: pointer;
  padding: var(--spacing-2);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: var(--radius-md);
  transition: var(--transition-fast);
  color: inherit;
  opacity: 0.8;
  flex-shrink: 0;
}

.close-button:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.1);
}

.close-button svg {
  width: 20px;
  height: 20px;
}

/* ============================================
   Body
   ============================================ */
.dialog-body-wrapper {
  overflow: hidden;
  min-height: 0;
  /* Smooth height transitions - animates actual height */
  transition: height 0.35s var(--ease-smooth),
              max-height 0.35s var(--ease-smooth);
  max-height: 60vh;
}

.dialog-body {
  padding: var(--spacing-6);
  overflow-y: auto;
  color: var(--color-gray-700);
  line-height: var(--line-height-relaxed);
  /* Smooth transitions */
  transition: padding var(--duration-base) ease;
}

.dialog-content-wrapper {
  transition: opacity 0.25s ease;
  display: block;
}

.dialog-body::-webkit-scrollbar {
  width: 8px;
}

.dialog-body::-webkit-scrollbar-track {
  background: var(--color-gray-100);
}

.dialog-body::-webkit-scrollbar-thumb {
  background: var(--color-gray-400);
  border-radius: var(--radius-sm);
}

.dialog-body::-webkit-scrollbar-thumb:hover {
  background: var(--color-gray-500);
}

/* ============================================
   Footer
   ============================================ */
.dialog-footer {
  padding: var(--spacing-4) var(--spacing-6);
  border-top: var(--border-light);
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-3);
  flex-wrap: wrap;
  background: var(--color-bg-secondary);
  /* Smooth transitions for footer appearance */
  transition: padding var(--duration-base) ease;
}

.footer-fade-enter-active {
  transition: opacity 0.25s ease, transform 0.25s ease, max-height 0.25s ease;
}

.footer-fade-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease, max-height 0.2s ease;
}

.footer-fade-enter-from {
  opacity: 0;
  transform: translateY(10px);
  max-height: 0;
}

.footer-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
  max-height: 0;
}

.footer-fade-enter-to,
.footer-fade-leave-from {
  opacity: 1;
  transform: translateY(0);
  max-height: 200px;
}

.dialog-button {
  padding: var(--spacing-3) var(--spacing-6);
  border: none;
  border-radius: var(--radius-md);
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
  cursor: pointer;
  transition: var(--transition-base);
  min-width: 100px;
}

.dialog-button-primary {
  background: var(--gradient-primary);
  color: var(--color-text-inverse);
  box-shadow: var(--shadow-primary-md);
}

.dialog-button-primary:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-primary-xl);
}

.dialog-button-primary:active {
  transform: translateY(0);
}

.dialog-button-secondary {
  background: var(--color-bg-secondary);
  color: var(--color-gray-700);
  border: var(--border-width-base) solid var(--color-border-light);
}

.dialog-button-secondary:hover {
  background: var(--color-bg-tertiary);
  border-color: var(--color-border-medium);
}

/* ============================================
   Animations - Smoother transitions
   ============================================ */
.dialog-fade-enter-active,
.dialog-fade-leave-active {
  transition: opacity 0.2s ease;
}

.dialog-fade-enter-from,
.dialog-fade-leave-to {
  opacity: 0;
}

.dialog-scale-enter-active {
  transition: all 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.dialog-scale-leave-active {
  transition: all 0.15s ease-in;
}

.dialog-scale-enter-from {
  opacity: 0;
  transform: scale(0.95) translateY(-10px);
}

.dialog-scale-leave-to {
  opacity: 0;
  transform: scale(0.98);
}

/* ============================================
   Content Fade Transition
   ============================================ */
.content-fade-enter-active,
.content-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.content-fade-enter-from {
  opacity: 0;
  transform: translateY(-5px);
}

.content-fade-leave-to {
  opacity: 0;
  transform: translateY(5px);
}

.content-fade-enter-to,
.content-fade-leave-from {
  opacity: 1;
  transform: translateY(0);
}

/* ============================================
   Title Fade Transition
   ============================================ */
.title-fade-enter-active,
.title-fade-leave-active {
  transition: opacity 0.2s ease;
}

.title-fade-enter-from,
.title-fade-leave-to {
  opacity: 0;
}

.title-fade-enter-to,
.title-fade-leave-from {
  opacity: 1;
}

/* ============================================
   Responsive Design
   ============================================ */
@media (max-width: 768px) {
  .dialog-overlay {
    padding: var(--spacing-2);
  }

  .dialog-container {
    max-width: 100%;
    border-radius: var(--radius-lg);
  }

  .dialog-header {
    padding: var(--spacing-5);
  }

  .dialog-title {
    font-size: var(--font-size-lg);
  }

  .dialog-body {
    padding: var(--spacing-5);
  }

  .dialog-footer {
    padding: var(--spacing-4);
    flex-direction: column;
  }

  .dialog-button {
    width: 100%;
  }
}

@media (max-width: 480px) {
  .dialog-header {
    padding: var(--spacing-4);
  }

  .dialog-title {
    font-size: var(--font-size-md);
  }

  .dialog-body {
    padding: var(--spacing-4);
  }

  .icon-wrapper {
    width: 28px;
    height: 28px;
  }

  .icon-wrapper svg {
    width: 20px;
    height: 20px;
  }
}
</style>

